import type { CSSProperties } from "react";

interface DataPoint {
  date: string;
  value: number;
}

interface MetricsChartProps {
  title: string;
  data: DataPoint[];
  accent?: "accent" | "sky";
  formatValue?: (v: number) => string;
}

const ACCENTS = {
  accent: {
    color: "var(--color-accent)",
    grad: "linear-gradient(180deg, var(--color-accent-glow) 0%, var(--color-accent) 60%, rgba(91, 33, 182, 0.4) 100%)",
    bright: "var(--color-accent-glow)",
  },
  sky: {
    color: "var(--color-status-running)",
    grad: "linear-gradient(180deg, #7dd3fc 0%, #38bdf8 60%, rgba(2, 132, 199, 0.4) 100%)",
    bright: "#7dd3fc",
  },
} as const;

export function MetricsChart({ title, data, accent = "accent", formatValue }: MetricsChartProps) {
  const a = ACCENTS[accent];

  if (data.length === 0) {
    return (
      <div className="panel p-5">
        <h3 className="eyebrow">{title}</h3>
        <p className="mt-3 text-sm text-[var(--color-text-dim)]">No data available</p>
      </div>
    );
  }

  const max = Math.max(...data.map((d) => d.value), 1);
  const total = data.reduce((s, d) => s + d.value, 0);
  const avg = total / data.length;
  const latest = data[data.length - 1]?.value ?? 0;
  const prior = data[data.length - 2]?.value ?? latest;
  const trend = prior === 0 ? 0 : ((latest - prior) / prior) * 100;
  const trendUp = trend >= 0;

  return (
    <div className="panel p-6 relative overflow-hidden">
      {/* corner accent */}
      <span className="absolute top-0 left-0 w-12 h-px" style={{ background: a.color }} />
      <span className="absolute top-0 left-0 w-px h-12" style={{ background: a.color }} />

      <div className="flex items-end justify-between mb-1">
        <div>
          <p className="eyebrow">{title}</p>
          <p className="font-display text-3xl italic mt-1 tabular-nums" style={{ color: a.bright }}>
            {formatValue ? formatValue(latest) : latest}
          </p>
        </div>
        <div className="text-right space-y-0.5">
          <p className="font-mono text-[10px] tracking-wider uppercase text-[var(--color-text-dim)]">
            14d avg · <span className="text-[var(--color-text-muted)]">{formatValue ? formatValue(Math.round(avg)) : Math.round(avg)}</span>
          </p>
          <p
            className="font-mono text-[10px] tracking-wider"
            style={{ color: trendUp ? "var(--color-status-active)" : "var(--color-status-error)" }}
          >
            {trendUp ? "▲" : "▼"} {Math.abs(trend).toFixed(1)}%
          </p>
        </div>
      </div>

      {/* Grid lines */}
      <div className="relative mt-5 h-32">
        {[0, 0.25, 0.5, 0.75, 1].map((p) => (
          <div
            key={p}
            className="absolute inset-x-0 border-t border-dashed border-[var(--color-border)]"
            style={{ top: `${p * 100}%`, opacity: p === 0 || p === 1 ? 0.6 : 0.3 }}
          />
        ))}

        {/* Bars */}
        <div className="absolute inset-0 flex items-end gap-[3px] px-px">
          {data.map((d, i) => {
            const height = (d.value / max) * 100;
            const finalH = Math.max(height, d.value > 0 ? 4 : 0);
            return (
              <div
                key={d.date}
                className="flex-1 h-full flex flex-col justify-end relative"
                title={`${d.date}: ${formatValue ? formatValue(d.value) : d.value}`}
              >
                <div
                  className="chart-bar"
                  style={{
                    "--bar-h": `${finalH}%`,
                    background: a.grad,
                    color: a.bright,
                    animationDelay: `${200 + i * 35}ms`,
                  } as CSSProperties}
                />
              </div>
            );
          })}
        </div>
      </div>

      {/* X-axis labels */}
      <div className="flex justify-between mt-2 font-mono text-[10px] tracking-wider text-[var(--color-text-dim)]">
        <span>{data[0]?.date}</span>
        <span>{data[Math.floor(data.length / 2)]?.date}</span>
        <span>{data[data.length - 1]?.date}</span>
      </div>
    </div>
  );
}
